import React, { useCallback, useEffect, useState } from 'react';
import CustomHeader from '../../components/header/index.jsx';
import PageHeader from '../../components/pageHeader';
import url from '../../config.js';
import './cloudaccounts.scss';

const API = `${url.backendUrl}/api/v1/external-cloud-accounts`;
const INFRA_API = `${url.backendUrl}/api/v1/external-infra`;
const token = () => localStorage.getItem('token');

async function request(path = '', options = {}) {
  const response = await fetch(`${API}${path}`, {
    ...options,
    headers: { 'x-access-token': token(), 'Content-Type': 'application/json', ...(options.headers || {}) },
  });
  const result = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(result.message || `Request failed (${response.status})`);
  return result;
}

async function infraRequest(path = '', options = {}) {
  const response = await fetch(`${INFRA_API}${path}`, {
    ...options,
    headers: { 'x-access-token': token(), 'Content-Type': 'application/json', ...(options.headers || {}) },
  });
  const result = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(result.message || `Request failed (${response.status})`);
  return result;
}

const planDigestOf = (t) => t && t.approval && t.approval.planDigest;

function downloadConfig(account) {
  const content = [
    `EXPECTED_AWS_ACCOUNT_ID=${account.awsAccountId}`,
    `STACK_NAME=${account.onboarding?.stackName || 'intelog-cloud-runner'}`,
    'ROLE_NAME=IntelogCloudRunner',
    'CONTROL_REGION=us-east-1',
    `GOOGLE_SERVICE_ACCOUNT_SUBJECT=${account.onboarding?.googleSubject || 'REQUEST_FROM_INTELOG'}`,
    `GOOGLE_AUTHORIZED_PARTY=${account.onboarding?.googleAuthorizedParty || 'REQUEST_FROM_INTELOG'}`,
    `TOKEN_AUDIENCE=${account.tokenAudience}`,
    `ALLOWED_REGION=${account.allowedRegions[0]}`,
    `PROVISIONING_PROFILE=${account.provisioningProfile || 'VpcSmoke'}`,
    `POLICY_VERSION=${account.policyVersion || '2026-10-02'}`,
    '',
  ].join('\n');
  const link = document.createElement('a');
  link.href = URL.createObjectURL(new Blob([content], { type: 'text/plain' }));
  link.download = `intelog-aws-${account.integrationId}.env`;
  link.click();
  URL.revokeObjectURL(link.href);
}

export default function CloudAccounts() {
  const [accounts, setAccounts] = useState([]);
  const [form, setForm] = useState({ awsAccountId: '', environment: 'production', allowedRegions: 'eu-west-1' });
  const [roles, setRoles] = useState({});
  const [permissionReview, setPermissionReview] = useState({});
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');

  const load = useCallback(async () => {
    try { setAccounts(await request()); } catch (error) { setMessage(error.message); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const begin = async (event) => {
    event.preventDefault(); setBusy(true); setMessage('');
    try {
      await request('/onboarding', { method: 'POST', body: JSON.stringify({
        ...form,
        allowedRegions: form.allowedRegions.split(',').map((v) => v.trim()).filter(Boolean),
      }) });
      setForm({ awsAccountId: '', environment: 'production', allowedRegions: 'eu-west-1' });
      await load();
    } catch (error) { setMessage(error.message); } finally { setBusy(false); }
  };

  const complete = async (account) => {
    const roleArn = (roles[account.id] || '').trim();
    if (!roleArn) return setMessage('Enter the CloudFormation role ARN first.');
    setBusy(true); setMessage('');
    try {
      await request(`/${account.id}/complete`, { method: 'POST', body: JSON.stringify({ roleArn, tokenAudience: account.tokenAudience }) });
      await load();
    } catch (error) { setMessage(error.message); } finally { setBusy(false); }
  };

  const verify = async (account) => {
    setBusy(true); setMessage('');
    try {
      await request(`/${account.id}/verify`, { method: 'POST' });
      await load();
    } catch (error) { setMessage(error.message); await load(); } finally { setBusy(false); }
  };

  const reviewEksPermissions = async (account) => {
    setBusy(true); setMessage('');
    try {
      const review = await request(`/${account.id}/permission-upgrade`, {
        method: 'POST', body: JSON.stringify({ requiredTemplates: ['aws-vpc', 'aws-eks'] }),
      });
      setPermissionReview((p) => ({ ...p, [account.id]: review }));
      await load();
    } catch (error) { setMessage(error.message); } finally { setBusy(false); }
  };

  const completePermissionUpgrade = async (account) => {
    const review = permissionReview[account.id];
    if (!review?.assessment) return setMessage('Review the permission update first.');
    setBusy(true); setMessage('');
    try {
      await request(`/${account.id}/permission-upgrade/complete`, { method: 'POST', body: JSON.stringify({
        provisioningProfile: review.assessment.requiredProfile,
        policyVersion: review.assessment.requiredPolicyVersion,
        allowedTemplates: review.assessment.requiredAllowedTemplates,
      }) });
      setPermissionReview((p) => ({ ...p, [account.id]: undefined }));
      await load();
    } catch (error) { setMessage(error.message); } finally { setBusy(false); }
  };

  const [clusterTask, setClusterTask] = useState({}); // { [accountId]: task }
  const [clusterBusy, setClusterBusy] = useState({}); // { [accountId]: bool }
  const [eksForm, setEksForm] = useState({});
  const [eksDiscovery, setEksDiscovery] = useState({});

  const pollTask = (accountId, taskId) => {
    const iv = setInterval(async () => {
      try {
        const t = await infraRequest(`/tasks/${taskId}`);
        setClusterTask((p) => ({ ...p, [accountId]: t }));
        // Stop polling on a terminal state (awaiting approval, or succeeded/failed/cancelled).
        if (t.status === 'awaiting_approval' || ['succeeded', 'failed', 'cancelled'].includes(t.status)) {
          clearInterval(iv);
        }
      } catch (e) { clearInterval(iv); setMessage(e.message); }
    }, 4000);
  };

  const approveCluster = async (account) => {
    const t = clusterTask[account.id];
    const digest = planDigestOf(t);
    if (!digest) return setMessage('No plan is ready to approve yet.');
    setClusterBusy((p) => ({ ...p, [account.id]: true })); setMessage('');
    try {
      const approved = await infraRequest(`/tasks/${t._id}/approve`, { method: 'POST', body: JSON.stringify({ planDigest: digest }) });
      setClusterTask((p) => ({ ...p, [account.id]: approved }));
      pollTask(account.id, approved._id);
    } catch (e) { setMessage(e.message); } finally { setClusterBusy((p) => ({ ...p, [account.id]: false })); }
  };

  const setEKS = (id, patch) => setEksForm((p) => ({
    ...p,
    [id]: {
      mode: 'existing', clusterName: '', vpcCidr: '10.2.0.0/16', nodeInstanceType: 't3.large',
      nodeMinSize: 2, nodeMaxSize: 4, nodeDesiredSize: 2, clusterVersion: '1.32', ...(p[id] || {}), ...patch,
    },
  }));

  const discoverClusters = async (account) => {
    setClusterBusy((p) => ({ ...p, [account.id]: true })); setMessage('');
    try {
      const task = await infraRequest('/eks-discoveries', { method: 'POST', body: JSON.stringify({
        targetAccountId: account.id, environment: account.environment, region: account.allowedRegions[0],
      }) });
      setEksDiscovery((p) => ({ ...p, [account.id]: task }));
      const iv = setInterval(async () => {
        try {
          const current = await infraRequest(`/tasks/${task._id}`);
          setEksDiscovery((p) => ({ ...p, [account.id]: current }));
          if (['succeeded', 'failed', 'cancelled'].includes(current.status)) clearInterval(iv);
        } catch (error) { clearInterval(iv); setMessage(error.message); }
      }, 4000);
    } catch (error) { setMessage(error.message); } finally { setClusterBusy((p) => ({ ...p, [account.id]: false })); }
  };

  const selectExistingCluster = async (account, clusterName) => {
    if (!clusterName) return setMessage('Choose an EKS cluster first.');
    setClusterBusy((p) => ({ ...p, [account.id]: true })); setMessage('');
    try {
      await request(`/${account.id}/eks-selection`, { method: 'POST', body: JSON.stringify({ clusterName, region: account.allowedRegions[0] }) });
      await load();
      setMessage(`Using existing EKS cluster: ${clusterName}.`);
    } catch (error) { setMessage(error.message); } finally { setClusterBusy((p) => ({ ...p, [account.id]: false })); }
  };

  const planEksCluster = async (account) => {
    const f = eksForm[account.id] || {};
    if (!f.clusterName) return setMessage('Enter an EKS cluster name.');
    setClusterBusy((p) => ({ ...p, [account.id]: true })); setMessage('');
    try {
      const task = await infraRequest('/design-tasks', { method: 'POST', body: JSON.stringify({
        targetAccountId: account.id, environment: account.environment, region: account.allowedRegions[0], stackKey: f.clusterName,
        design: {
          components: ['vpc', 'eks', 'ecr'], vpcCidr: f.vpcCidr, nodeInstanceType: f.nodeInstanceType,
          nodeMinSize: Number(f.nodeMinSize), nodeMaxSize: Number(f.nodeMaxSize), nodeDesiredSize: Number(f.nodeDesiredSize),
          clusterVersion: f.clusterVersion, tags: { ManagedBy: 'Intelog' },
        },
        idempotencyKey: `${account.id}:${f.clusterName}:${account.allowedRegions[0]}:${Date.now()}`,
      }) });
      setClusterTask((p) => ({ ...p, [account.id]: task }));
      pollTask(account.id, task._id);
    } catch (error) { setMessage(error.message); } finally { setClusterBusy((p) => ({ ...p, [account.id]: false })); }
  };

  return <div className="root">
    <CustomHeader />
    <main className="content">
    <div className="ca-page">
      <div className="ca-heading">
        <PageHeader eyebrow="AWS onboarding" title="Cloud Accounts" subtitle="Connect customer AWS accounts without storing access keys." />
      </div>
      {message && <div className="ca-message">{message}</div>}
      <section className="ca-card">
        <h2>Connect AWS account</h2>
        <form className="ca-form" onSubmit={begin}>
          <label>AWS account ID<input required pattern="[0-9]{12}" value={form.awsAccountId} onChange={(e) => setForm({ ...form, awsAccountId: e.target.value })} placeholder="123456789012" /></label>
          <label>Environment<select value={form.environment} onChange={(e) => setForm({ ...form, environment: e.target.value })}><option value="development">Development</option><option value="staging">Staging</option><option value="production">Production</option></select></label>
          <label>Approved regions<input required value={form.allowedRegions} onChange={(e) => setForm({ ...form, allowedRegions: e.target.value })} placeholder="eu-west-1, us-east-1" /></label>
          <button disabled={busy}>Generate onboarding configuration</button>
        </form>
      </section>
      <section className="ca-list">
        {accounts.length === 0 && <div className="ca-card">No AWS cloud accounts connected yet.</div>}
        {accounts.map((account) => <article className="ca-card" key={account.id}>
          <div className="ca-row"><div><h2>{account.awsAccountId}</h2><small>{account.environment} · {account.allowedRegions.join(', ')}</small></div><strong className={`ca-status ca-${account.status}`}>{account.status.replaceAll('_', ' ')}</strong></div>
          <dl><dt>Token audience</dt><dd>{account.tokenAudience}</dd>{account.roleArn && <><dt>Role ARN</dt><dd>{account.roleArn}</dd></>}</dl>
          {account.status === 'pending_customer_setup' && <div className="ca-actions">
            <button type="button" onClick={() => downloadConfig(account)}>Download customer config</button>
            {account.onboarding?.quickCreateUrl && <a href={account.onboarding.quickCreateUrl} target="_blank" rel="noreferrer">Open AWS Quick Create</a>}
            <a href={account.onboarding?.repository || 'https://github.com/IntelogAI/aws-cloud-runner-onboarding-package'} target="_blank" rel="noreferrer">Open onboarding package</a>
            <input value={roles[account.id] || ''} onChange={(e) => setRoles({ ...roles, [account.id]: e.target.value })} placeholder="Paste CloudFormation RoleArn output" />
            <button type="button" disabled={busy} onClick={() => complete(account)}>Submit for verification</button>
          </div>}
          {account.status === 'pending_verification' && <div className="ca-actions">
            <p className="ca-note">Configuration received. Click Verify to assume the federated role and activate this account.</p>
            <button type="button" disabled={busy} onClick={() => verify(account)}>{busy ? 'Verifying…' : 'Verify now'}</button>
          </div>}
          {account.status === 'verification_failed' && <div className="ca-actions">
            <p className="ca-note" style={{ color: '#ffb4b4' }}>Verification failed{account.verificationError ? `: ${account.verificationError}` : ''}</p>
            <button type="button" disabled={busy} onClick={() => verify(account)}>{busy ? 'Verifying…' : 'Retry verification'}</button>
          </div>}
          {account.status === 'active' && (() => {
            const t = clusterTask[account.id];
            const eks = eksForm[account.id] || { mode: 'existing', clusterName: '', vpcCidr: '10.2.0.0/16', nodeInstanceType: 't3.large', nodeMinSize: 2, nodeMaxSize: 4, nodeDesiredSize: 2, clusterVersion: '1.32' };
            const discovery = eksDiscovery[account.id];
            const clusters = discovery?.discovery?.clusters || [];
            const b = !!clusterBusy[account.id];
            const digest = planDigestOf(t);
            const review = permissionReview[account.id];
            return <div className="ca-actions">
              <p className="ca-note" style={{ color: '#8af0b0' }}>✓ Active — verified{account.lastVerifiedAt ? ` ${new Date(account.lastVerifiedAt).toLocaleString()}` : ''}.</p>
              <small>Permission profile: {account.provisioningProfile || 'VpcSmoke'} · policy {account.policyVersion || '2026-10-02'} · templates: {(account.allowedTemplates || ['aws-vpc']).join(', ')}</small>
              {account.permissionUpdate?.requiredProfile && <p className="ca-note" style={{ color: '#ffd98a' }}>Permission update requested: {account.permissionUpdate.requiredProfile} · policy {account.permissionUpdate.requiredPolicyVersion}</p>}
              {!(account.allowedTemplates || ['aws-vpc']).includes('aws-eks') && <>
                <button type="button" disabled={busy} onClick={() => reviewEksPermissions(account)}>Review EKS permission update</button>
                {review?.assessment && <div className="ca-note">
                  <p>Required profile: <strong>{review.assessment.requiredProfile}</strong> · policy {review.assessment.requiredPolicyVersion}</p>
                  <p>Adds templates: {review.assessment.missingTemplates.join(', ')}</p>
                  {review.quickCreateUrl
                    ? <a href={review.quickCreateUrl} target="_blank" rel="noreferrer">Open AWS Quick Create</a>
                    : <a href={review.cloudFormationConsoleUrl} target="_blank" rel="noreferrer">Open existing stack in AWS CloudFormation</a>}
                  {review.templateUrl && <><small>Use the versioned template URL shown by Intelog when updating the existing stack.</small><a href={review.templateUrl} target="_blank" rel="noreferrer">View versioned template</a></>}
                  {review.onboardingRepository && <a href={review.onboardingRepository} target="_blank" rel="noreferrer">Download versioned onboarding package</a>}
                  <button type="button" disabled={busy} onClick={() => completePermissionUpgrade(account)}>I completed the CloudFormation update</button>
                </div>}
              </>}
              {(account.allowedTemplates || ['aws-vpc']).includes('aws-eks') && <section className="eks-flow">
                <div className="eks-flow-heading"><div><h3>EKS cluster</h3><p className="ca-note">Use an existing cluster or create a new managed platform cluster.</p></div><button type="button" disabled={b} onClick={() => discoverClusters(account)}>{discovery?.status === 'discovering' ? 'Finding clusters…' : 'Refresh clusters'}</button></div>
                {discovery?.status === 'failed' && <p className="ca-note eks-error">Could not list EKS clusters{discovery.errorSummary ? `: ${discovery.errorSummary}` : '.'}</p>}
                {discovery?.status === 'succeeded' && <>
                  <div className="eks-choice" role="radiogroup" aria-label="EKS cluster choice">
                    <label><input type="radio" name={`eks-mode-${account.id}`} checked={eks.mode === 'existing'} onChange={() => setEKS(account.id, { mode: 'existing' })} /> Use an existing cluster</label>
                    <label><input type="radio" name={`eks-mode-${account.id}`} checked={eks.mode === 'new'} onChange={() => setEKS(account.id, { mode: 'new' })} /> Create a new cluster</label>
                  </div>
                  {eks.mode === 'existing' && <div className="eks-fields">
                    <label>Available clusters<select value={eks.clusterName} onChange={(e) => setEKS(account.id, { clusterName: e.target.value })}><option value="">Select a cluster</option>{clusters.map((cluster) => <option value={cluster.name} key={cluster.arn || cluster.name}>{cluster.name} · {cluster.version || 'unknown version'} · {cluster.status || 'unknown status'}</option>)}</select></label>
                    {!clusters.length && <p className="ca-note">No EKS clusters were found in {(account.allowedRegions || [])[0]}.</p>}
                    <button type="button" disabled={b || !eks.clusterName} onClick={() => selectExistingCluster(account, eks.clusterName)}>Use selected cluster</button>
                    {account.selectedEksCluster && <p className="ca-note eks-success">✓ Using {account.selectedEksCluster} in {account.selectedEksClusterRegion}.</p>}
                  </div>}
                  {eks.mode === 'new' && <div className="eks-fields eks-create-grid">
                    <label>Cluster name<input value={eks.clusterName} onChange={(e) => setEKS(account.id, { clusterName: e.target.value.toLowerCase() })} placeholder="intelog-production" /></label>
                    <label>VPC CIDR<input value={eks.vpcCidr} onChange={(e) => setEKS(account.id, { vpcCidr: e.target.value })} /></label>
                    <label>Instance type<input value={eks.nodeInstanceType} onChange={(e) => setEKS(account.id, { nodeInstanceType: e.target.value })} /></label>
                    <label>Min nodes<input type="number" min="1" value={eks.nodeMinSize} onChange={(e) => setEKS(account.id, { nodeMinSize: e.target.value })} /></label>
                    <label>Max nodes<input type="number" min="1" value={eks.nodeMaxSize} onChange={(e) => setEKS(account.id, { nodeMaxSize: e.target.value })} /></label>
                    <label>Initial nodes<input type="number" min="1" value={eks.nodeDesiredSize} onChange={(e) => setEKS(account.id, { nodeDesiredSize: e.target.value })} /></label>
                    <label>Kubernetes version<input value={eks.clusterVersion} onChange={(e) => setEKS(account.id, { clusterVersion: e.target.value })} /></label>
                    <button type="button" disabled={b || !eks.clusterName} onClick={() => planEksCluster(account)}>{b ? 'Planning…' : 'Plan new cluster'}</button>
                  </div>}
                </>}
                {!discovery && <p className="ca-note">Refresh clusters to choose an existing cluster or reveal the creation form.</p>}
              </section>}
              {t && <>
                <div className="ca-row"><small>Task {t._id}</small><strong className={`ca-status ca-${t.status}`}>{String(t.status || '').replaceAll('_', ' ')}</strong></div>
                {t.status === 'awaiting_approval' && digest && <>
                  <p className="ca-note">{t.operation === 'destroy-plan' ? 'Destroy plan' : 'Plan'} ready (digest {String(digest).slice(0, 12)}…). Review, then approve.</p>
                  <button type="button" disabled={b} onClick={() => approveCluster(account)}>{b ? 'Applying…' : 'Approve & create EKS cluster'}</button>
                </>}
                {t.status === 'succeeded' && t.operation === 'apply' && <>
                  <p className="ca-note eks-success">✓ EKS cluster provisioned.</p>
                </>}
                {(t.status === 'failed' || t.status === 'cancelled') && <p className="ca-note" style={{ color: '#ffb4b4', whiteSpace: 'pre-wrap' }}>
                  EKS task {t.status}{t.errorSummary ? `: ${t.errorSummary}` : '.'}
                  {t.logsUri && <> — <a href={t.logsUri} target="_blank" rel="noreferrer">runner logs</a></>}
                </p>}
                <button type="button" onClick={() => setClusterTask((p) => ({ ...p, [account.id]: undefined }))} style={{ background: 'transparent', border: '1px solid #2a3f57', color: '#a9b7c6' }}>Create another</button>
              </>}
            </div>;
          })()}
        </article>)}
      </section>
    </div>
    </main>
  </div>;
}
